import { faLongArrowLeft, faPlus } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { TabKey, TabWorkMap } from '..';
import { StaticTableColumn } from '../../../../components/common/table/static-table';
import { LinkComponent } from '../../../../components/form/elements/link';
import { CropWorkType } from '../../../../enums/crop-work-types';
import Cargo from '../../../../interfaces/cargo';
import {
  CROP_YIELD_ACCOUNTING_INDEX,
  CROP_YIELD_ACCOUNTING_CARGO_CREATE,
  CROP_YIELD_ACCOUNTING_CARGO_VIEW,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { route } from '../../../../services/utils';
import moment from 'moment';
import css from './index.module.css';
import StaticTable, {
  loadingStaticTable,
} from '../../../../components/common/table/static-table';
import CargosTable from '../../../../interfaces/cargosTable';
import CargoInfo from '../../../../interfaces/cargo-info';
import ShowIfAllowed from '../../../../components/common/show-if-allowed';

export default function YieldAccountingCargoIndex() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.yield_accounting.cargo.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId, season, yield_id } = useParams<{
    farmId: string;
    season: string;
    yield_id: string;
  }>();
  const { cargoService } = useService();
  const history = useHistory();
  const [indexInfo, setIndexInfo] = useState(null);
  const [tables, setTables] = useState(null);

  useEffect(() => {
    cargoService.indexInfo(farmId, yield_id).subscribe(
      (response: CargoInfo) => {
        setIndexInfo(response);
      },
      () => {
        history.push(
          route(CROP_YIELD_ACCOUNTING_INDEX, {
            farmId: farmId,
            season: season,
            tab: TabKey.GRAINS,
          })
        );
      }
    );
  }, [history, season, cargoService, farmId, yield_id]);

  useEffect(() => {
    if (indexInfo) {
      const columnOrder = [
        'workField.crop_value',
        'workField.breed',
        'product_type_value',
        'gross',
        'tare',
        'net',
        'impurities_total',
        'humidity_above_norm',
        'quantity',
        'warehouses_quantity_sum',
      ];

      const columnsBase: StaticTableColumn<Cargo>[] = [
        {
          accessor: 'workField.crop_value',
          header: t('pages.crop.fields_and_works.fields.crop'),
        },
        {
          accessor: 'workField.breed',
          header: t('pages.crop.fields_and_works.fields.breed'),
        },
        {
          accessor: 'product_type_value',
          header: tp('product_type'),
        },
        {
          accessor: ['gross', 'unit'],
          aggregateAccessor: 'gross',
          header: tp('gross'),
        },
        {
          accessor: ['tare', 'unit'],
          header: tp('tare'),
        },
        {
          accessor: ['quantity', 'unit'],
          aggregateAccessor: 'quantity',
          header: tp('quantity'),
        },
        {
          accessor: ['warehouses_quantity_sum', 'unit'],
          aggregateAccessor: 'warehouses_quantity_sum',
          header: tp('warehouses_quantity_sum'),
        },
      ];

      const columnsThreshing: StaticTableColumn<Cargo>[] = [
        {
          accessor: ['net', 'unit'],
          aggregateAccessor: 'net',
          header: tp('net'),
        },
        {
          accessor: ['impurities_total', 'unit'],
          aggregateAccessor: 'impurities_total',
          header: tp('impurities_total'),
        },
        {
          accessor: ['humidity_above_norm', 'unit'],
          aggregateAccessor: 'humidity_above_norm',
          header: tp('humidity_above_norm'),
        },
      ];

      const columns = columnsBase
        .concat(
          indexInfo['work_type_id'] === CropWorkType.THRESHING
            ? columnsThreshing
            : []
        )
        .sort(function (a: any, b: any) {
          const aValue = Array.isArray(a.accessor) ? a.accessor[0] : a.accessor;
          const bValue = Array.isArray(b.accessor) ? b.accessor[0] : b.accessor;
          return columnOrder.indexOf(aValue) - columnOrder.indexOf(bValue);
        });

      cargoService
        .index(farmId, yield_id, {})
        .subscribe((response: CargosTable[]) => {
          if (response.length === 0) {
            setTables(<StaticTable<Cargo> columns={columns} />);
          } else {
            setTables(
              response.map((group: any) => {
                return (
                  <StaticTable<Cargo>
                    data={group}
                    keyAccessor="id"
                    columns={columns}
                    aggregateLabels={{ sum: tp('sum'), avg: tp('avg') }}
                    viewRoute={route(CROP_YIELD_ACCOUNTING_CARGO_VIEW, {
                      farmId: farmId,
                      season: season,
                      yieldId: yield_id,
                    })}
                    viewRecordAbility={{ title: 'view cargo' }}
                  />
                );
              })
            );
          }
        });
    }
  }, [cargoService, farmId, season, yield_id, t, tp, indexInfo, history]);

  if (!indexInfo) {
    return null;
  }

  return (
    <div>
      <div className={css['body']}>
        <div className={css['toolbar']}>
          <div className={css['left']}>
            <div>
              <LinkComponent
                label="back"
                linkTo={route(CROP_YIELD_ACCOUNTING_INDEX, {
                  farmId: farmId,
                  season: season,
                  tab: Object.keys(TabWorkMap).find(
                    (tab) =>
                      TabWorkMap[tab as TabKey] === indexInfo['work_type_id']
                  ),
                })}
                icon={faLongArrowLeft}
              />
              <h2>
                {tp('title')}: {indexInfo['field']}
              </h2>
            </div>
          </div>
          <div className={css['right']}>
            <div>
              <ShowIfAllowed ability={{ title: 'create cargo' }}>
                <LinkComponent
                  label="add"
                  primary={true}
                  linkTo={route(CROP_YIELD_ACCOUNTING_CARGO_CREATE, {
                    farmId: farmId,
                    season: season,
                    yieldId: yield_id,
                  })}
                  icon={faPlus}
                />
              </ShowIfAllowed>
            </div>
          </div>
        </div>
        <h3>
          {t(
            'classifiers.properties.crop_work_type.'.concat(
              indexInfo['work_type_id']
            )
          )}
          <span className={css['addition']}>
            {moment(indexInfo['work_date']).format('DD.MM.YYYY')}
          </span>
        </h3>
        {tables ?? loadingStaticTable()}
      </div>
    </div>
  );
}
